import { makeAutoObservable } from "mobx";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { truncateUidForLogging } from "@Front/Utils/loggingHelpers";

export interface CustomerOffice {
	uid: string;
	name: string;
	folderCount: number;
}

/**
 * Store MobX pour gérer l'office actif des clients et tiers
 * Basé sur les offices extraits des dossiers qu'ils ont
 */
class CustomerOfficeStore {
	public availableOffices: CustomerOffice[] = [];
	public activeOfficeUid: string | null = null;
	private readonly STORAGE_KEY = "customer_active_office_uid";

	constructor() {
		makeAutoObservable(this);
		// Charger l'office actif depuis localStorage au démarrage (uniquement côté client)
		if (typeof window !== "undefined") {
			this.loadActiveOfficeFromStorage();
		}
	}

	/**
	 * Charge l'office actif depuis localStorage
	 */
	private loadActiveOfficeFromStorage(): void {
		if (typeof window === "undefined" || typeof localStorage === "undefined") {
			return;
		}
		try {
			const stored = localStorage.getItem(this.STORAGE_KEY);
			if (stored) {
				this.activeOfficeUid = stored;
				LoggerService.info("🔍 [CustomerOfficeStore] Office actif chargé depuis localStorage", {
					activeOfficeUid: stored.substring(0, 8) + "...",
				});
			}
		} catch (error) {
			LoggerService.warn("⚠️ [CustomerOfficeStore] Erreur lors du chargement depuis localStorage", { error });
		}
	}

	/**
	 * Sauvegarde l'office actif dans localStorage
	 */
	private saveActiveOfficeToStorage(officeUid: string | null): void {
		if (typeof window === "undefined" || typeof localStorage === "undefined") {
			return;
		}
		try {
			if (officeUid) {
				localStorage.setItem(this.STORAGE_KEY, officeUid);
			} else {
				localStorage.removeItem(this.STORAGE_KEY);
			}
		} catch (error) {
			LoggerService.warn("⚠️ [CustomerOfficeStore] Erreur lors de la sauvegarde dans localStorage", { error });
		}
	}

	/**
	 * Met à jour les offices disponibles depuis les dossiers
	 * @param foldersByOffice Liste des dossiers groupés par office
	 */
	public updateOfficesFromFolders(foldersByOffice: Array<{ office_uid: string; office_name: string; folders: unknown[] }>): void {
		const offices: CustomerOffice[] = foldersByOffice.map((group) => ({
			uid: group.office_uid,
			name: group.office_name,
			folderCount: group.folders.length,
		}));

		LoggerService.info("🔍 [CustomerOfficeStore] Mise à jour des offices depuis les dossiers", {
			officesCount: offices.length,
			offices: offices.map((o) => ({ uid: truncateUidForLogging(o.uid), name: o.name, folderCount: o.folderCount })),
		});

		this.availableOffices = offices;

		// Si aucun office actif n'est défini et qu'il y a des offices, définir le premier comme actif
		if (!this.activeOfficeUid && offices.length > 0) {
			const firstOffice = offices[0];
			if (firstOffice) {
				this.setActiveOffice(firstOffice.uid);
			}
		} else if (this.activeOfficeUid) {
			// Vérifier que l'office actif existe toujours dans la liste
			const activeOfficeExists = offices.some((o) => o.uid === this.activeOfficeUid);
			if (!activeOfficeExists) {
				LoggerService.warn("⚠️ [CustomerOfficeStore] Office actif n'existe plus, réinitialisation", {
					previousActiveOfficeUid: this.activeOfficeUid ? this.activeOfficeUid.substring(0, 8) + "..." : "null",
				});
				if (offices.length > 0 && offices[0]) {
					this.setActiveOffice(offices[0].uid);
				} else {
					this.activeOfficeUid = null;
					this.saveActiveOfficeToStorage(null);
				}
			}
		}
	}

	/**
	 * Définit l'office actif
	 * @param officeUid UID de l'office à activer
	 */
	public setActiveOffice(officeUid: string | null): void {
		if (officeUid === this.activeOfficeUid) {
			return;
		}

		// Vérifier que l'office existe dans la liste
		if (officeUid && !this.availableOffices.some((o) => o.uid === officeUid)) {
			LoggerService.warn("⚠️ [CustomerOfficeStore] Tentative d'activation d'un office inexistant", {
				officeUid: officeUid.substring(0, 8) + "...",
				availableOffices: this.availableOffices.map((o) => truncateUidForLogging(o.uid)),
			});
			return;
		}

		this.activeOfficeUid = officeUid;
		this.saveActiveOfficeToStorage(officeUid);

		LoggerService.info("✅ [CustomerOfficeStore] Office actif changé", {
			officeUid: officeUid?.substring(0, 8) + "...",
			officeName: officeUid ? this.availableOffices.find((o) => o.uid === officeUid)?.name : null,
		});
	}

	/**
	 * Récupère l'office actif
	 */
	public getActiveOffice(): CustomerOffice | null {
		if (!this.activeOfficeUid) {
			return null;
		}
		return this.availableOffices.find((o) => o.uid === this.activeOfficeUid) ?? null;
	}

	/**
	 * Vérifie si l'utilisateur a plusieurs offices
	 */
	public hasMultipleOffices(): boolean {
		return this.availableOffices.length > 1;
	}

	/**
	 * Réinitialise le store
	 */
	public reset(): void {
		this.availableOffices = [];
		this.activeOfficeUid = null;
		if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
			localStorage.removeItem(this.STORAGE_KEY);
		}
	}
}

let _customerOfficeStoreInstance: CustomerOfficeStore | null = null;

export function getCustomerOfficeStore(): CustomerOfficeStore {
	if (!_customerOfficeStoreInstance) {
		_customerOfficeStoreInstance = new CustomerOfficeStore();
	}
	return _customerOfficeStoreInstance;
}

// Keep backward compatibility - use lazy Proxy creation via IIFE to avoid module-level initialization
// This prevents "Cannot access 'o' before initialization" errors during Next.js build
let _customerOfficeStoreBackwardCompat: CustomerOfficeStore | undefined;
let _customerOfficeStoreProxy: CustomerOfficeStore | undefined;

function getCustomerOfficeStoreInstance(): CustomerOfficeStore {
	if (!_customerOfficeStoreBackwardCompat) {
		_customerOfficeStoreBackwardCompat = getCustomerOfficeStore();
	}
	return _customerOfficeStoreBackwardCompat;
}

function getCustomerOfficeStoreProxy(): CustomerOfficeStore {
	if (!_customerOfficeStoreProxy) {
		_customerOfficeStoreProxy = new Proxy({} as CustomerOfficeStore, {
			get(_target, prop): unknown {
				return (getCustomerOfficeStoreInstance() as unknown as Record<string, unknown>)[prop as string];
			},
			set(_target, prop, value): boolean {
				(getCustomerOfficeStoreInstance() as unknown as Record<string, unknown>)[prop as string] = value;
				return true;
			},
		});
	}
	return _customerOfficeStoreProxy;
}

// Export a Proxy that lazily creates the inner Proxy when first accessed
// The outer Proxy is created at module level, but the inner Proxy (which depends on getCustomerOfficeStore)
// is only created when first accessed, avoiding module-level initialization issues
export const customerOfficeStore = new Proxy({} as CustomerOfficeStore, {
	get(_target, prop): unknown {
		// Lazy initialization: create the inner Proxy only when first accessed
		return (getCustomerOfficeStoreProxy() as unknown as Record<string, unknown>)[prop as string];
	},
	set(_target, prop, value): boolean {
		// Lazy initialization: create the inner Proxy only when first accessed
		(getCustomerOfficeStoreProxy() as unknown as Record<string, unknown>)[prop as string] = value;
		return true;
	},
});
